import { parser } from "./parser";
import {
  Document,
  Object as ObjectNode,
  Property,
  PropertyKey,
  String as StringNode,
  Array as ArrayNode,
  Number as NumberNode,
  Boolean as BooleanNode,
  Null,
  BsonConstructor,
  BsonType,
  ArgList,
  BsonKeyword,
  Regex,
  TemplateVariable,
  SpecialNumber,
} from "./parser.terms";

// Derive TreeCursor type from the parser without importing @lezer/common directly
type TreeCursor = ReturnType<ReturnType<typeof parser.parse>["cursor"]>;

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export interface TransformResult {
  ejson: string;
  errors: string[];
}

export interface VariableResolver {
  resolveVariable?: (name: string) => string | undefined;
  resolveSecret?: (key: string) => string | undefined;
  resolveConfig?: (key: string) => string | undefined;
}

// ---------------------------------------------------------------------------
// Entry point
// ---------------------------------------------------------------------------

export function mongoShellToEjson(
  source: string,
  resolver?: VariableResolver,
): TransformResult {
  const errors: string[] = [];
  const tree = parser.parse(source);

  // Detect parse errors (error nodes = type 0 in Lezer)
  tree.iterate({
    enter(node) {
      if (node.type.isError) {
        const snippet = source.slice(node.from, Math.min(node.to, node.from + 20));
        errors.push(`Parse error at offset ${node.from}: unexpected "${snippet}"`);
      }
    },
  });

  // Transform starting from root
  const rootCursor = tree.cursor();
  const ejson = transformNode(rootCursor, source, resolver, errors);

  return { ejson, errors };
}

// ---------------------------------------------------------------------------
// Core recursive transformer
// ---------------------------------------------------------------------------

function transformNode(
  cursor: TreeCursor,
  source: string,
  resolver: VariableResolver | undefined,
  errors: string[],
): string {
  const type = cursor.type.id;

  switch (type) {
    case Document:
      return transformChildren(cursor, source, resolver, errors);

    case ObjectNode:
      return transformObject(cursor, source, resolver, errors);

    case ArrayNode:
      return transformArray(cursor, source, resolver, errors);

    case Property:
      return transformProperty(cursor, source, resolver, errors);

    case StringNode:
      return transformString(cursor, source);

    case NumberNode:
      return text(cursor, source);

    case BooleanNode:
      return text(cursor, source);

    case Null:
      return "null";

    case BsonConstructor:
      return transformBsonConstructor(cursor, source, resolver, errors);

    case BsonKeyword:
      return transformBsonKeyword(cursor, source);

    case Regex:
      return transformRegex(cursor, source);

    case TemplateVariable:
      return transformTemplateVariable(cursor, source, resolver, errors);

    case SpecialNumber:
      return transformSpecialNumber(cursor, source);

    default:
      // Fallback: raw text for unknown nodes
      return text(cursor, source);
  }
}

// ---------------------------------------------------------------------------
// Object  { Property* }
// ---------------------------------------------------------------------------

function transformObject(
  cursor: TreeCursor,
  source: string,
  resolver: VariableResolver | undefined,
  errors: string[],
): string {
  const parts: string[] = [];

  if (cursor.firstChild()) {
    do {
      const id = cursor.type.id;
      // Skip structural tokens: { } , and whitespace/comments (auto-skipped)
      if (id === Property) {
        parts.push(transformProperty(cursor, source, resolver, errors));
      }
      // Ignore punctuation ( { } , ) and comments
    } while (cursor.nextSibling());
    cursor.parent();
  }

  return `{${parts.join(",")}}`;
}

// ---------------------------------------------------------------------------
// Array  [ value* ]
// ---------------------------------------------------------------------------

function transformArray(
  cursor: TreeCursor,
  source: string,
  resolver: VariableResolver | undefined,
  errors: string[],
): string {
  const items: string[] = [];

  if (cursor.firstChild()) {
    do {
      const id = cursor.type.id;
      if (isValueNode(id)) {
        items.push(transformNode(cursor, source, resolver, errors));
      }
    } while (cursor.nextSibling());
    cursor.parent();
  }

  return `[${items.join(",")}]`;
}

// ---------------------------------------------------------------------------
// Property  PropertyKey : value
// ---------------------------------------------------------------------------

function transformProperty(
  cursor: TreeCursor,
  source: string,
  resolver: VariableResolver | undefined,
  errors: string[],
): string {
  let key = "";
  let value = "";

  if (cursor.firstChild()) {
    do {
      const id = cursor.type.id;
      if (id === PropertyKey) {
        key = transformPropertyKey(cursor, source);
      } else if (isValueNode(id)) {
        value = transformNode(cursor, source, resolver, errors);
      }
    } while (cursor.nextSibling());
    cursor.parent();
  }

  return `${key}:${value}`;
}

// ---------------------------------------------------------------------------
// PropertyKey — ensure double-quoted
// ---------------------------------------------------------------------------

function transformPropertyKey(cursor: TreeCursor, source: string): string {
  // PropertyKey can be either a String or a bare identifier (propertyName)
  if (cursor.firstChild()) {
    const id = cursor.type.id;
    if (id === StringNode) {
      const result = transformString(cursor, source);
      cursor.parent();
      return result;
    }
    cursor.parent();
  }

  // Bare property name — wrap in double quotes
  const raw = text(cursor, source);
  return `"${raw}"`;
}

// ---------------------------------------------------------------------------
// String — normalize to double-quoted
// ---------------------------------------------------------------------------

function transformString(cursor: TreeCursor, source: string): string {
  const raw = text(cursor, source);
  if (raw.startsWith('"')) {
    // Already double-quoted — pass through as-is
    return raw;
  }
  if (raw.startsWith("'")) {
    // Single-quoted → double-quoted
    // Remove outer single quotes, handle inner escaping
    const inner = raw.slice(1, -1);
    // Unescape escaped single quotes, escape unescaped double quotes
    const converted = inner
      .replace(/\\'/g, "'") // unescape \'
      .replace(/"/g, '\\"'); // escape " that were unescaped in the original
    return `"${converted}"`;
  }
  // Fallback (should not happen with valid parse)
  return `"${raw}"`;
}

// ---------------------------------------------------------------------------
// BsonConstructor — the core BSON → EJSON transformation
// ---------------------------------------------------------------------------

function transformBsonConstructor(
  cursor: TreeCursor,
  source: string,
  resolver: VariableResolver | undefined,
  errors: string[],
): string {
  let typeName = "";
  const args: string[] = [];

  if (cursor.firstChild()) {
    do {
      const id = cursor.type.id;
      if (id === BsonType) {
        typeName = text(cursor, source);
      } else if (id === ArgList) {
        collectArgs(cursor, source, resolver, errors, args);
      }
    } while (cursor.nextSibling());
    cursor.parent();
  }

  return emitBsonEjson(typeName, args, errors);
}

/** Collect transformed argument values from an ArgList node. */
function collectArgs(
  cursor: TreeCursor,
  source: string,
  resolver: VariableResolver | undefined,
  errors: string[],
  out: string[],
): void {
  if (cursor.firstChild()) {
    do {
      const id = cursor.type.id;
      if (isValueNode(id)) {
        out.push(transformNode(cursor, source, resolver, errors));
      }
    } while (cursor.nextSibling());
    cursor.parent();
  }
}

// ---------------------------------------------------------------------------
// BSON → EJSON emission
// ---------------------------------------------------------------------------

function emitBsonEjson(
  typeName: string,
  args: string[],
  errors: string[],
): string {
  switch (typeName) {
    case "ObjectId": {
      const val = unquote(args[0] ?? "");
      return `{"$oid":"${val}"}`;
    }

    case "ISODate": {
      const raw = unquote(args[0] ?? "");
      const date = normalizeDate(raw);
      if (!date) {
        errors.push(`Invalid date value: "${raw}"`);
        return `{"$date":"${raw}"}`;
      }
      return `{"$date":"${date}"}`;
    }

    case "NumberLong": {
      const val = unquote(args[0] ?? "0");
      return `{"$numberLong":"${val}"}`;
    }

    case "NumberInt": {
      // NumberInt emits as a plain JSON number
      const val = unquote(args[0] ?? "0");
      return val;
    }

    case "NumberDecimal": {
      const val = unquote(args[0] ?? "0");
      return `{"$numberDecimal":"${val}"}`;
    }

    case "UUID": {
      const val = unquote(args[0] ?? "");
      return `{"$uuid":"${val}"}`;
    }

    case "Timestamp": {
      const t = args[0] ?? "0";
      const i = args[1] ?? "0";
      return `{"$timestamp":{"t":${t},"i":${i}}}`;
    }

    case "BinData": {
      // BinData(subtype, base64)
      const subtype = unquote(args[0] ?? "0");
      const b64 = unquote(args[1] ?? "");
      const subHex = Number(subtype).toString(16).padStart(2, "0");
      return `{"$binary":{"base64":"${b64}","subType":"${subHex}"}}`;
    }

    case "Binary": {
      // Binary(subtype, base64) — same as BinData
      const subtype = unquote(args[0] ?? "0");
      const b64 = unquote(args[1] ?? "");
      const subHex = Number(subtype).toString(16).padStart(2, "0");
      return `{"$binary":{"base64":"${b64}","subType":"${subHex}"}}`;
    }

    case "DBRef": {
      // DBRef('collection', objectIdValue)
      const coll = unquote(args[0] ?? "");
      const id = args[1] ?? "null";
      return `{"$ref":"${coll}","$id":${id}}`;
    }

    default: {
      errors.push(`Unknown BSON constructor: ${typeName}`);
      return `"${typeName}()"`;
    }
  }
}

// ---------------------------------------------------------------------------
// BsonKeyword — MinKey / MaxKey
// ---------------------------------------------------------------------------

function transformBsonKeyword(cursor: TreeCursor, source: string): string {
  const kw = text(cursor, source);
  if (kw === "MinKey") return '{"$minKey":1}';
  if (kw === "MaxKey") return '{"$maxKey":1}';
  return `"${kw}"`;
}

// ---------------------------------------------------------------------------
// SpecialNumber — Infinity, -Infinity, NaN
// ---------------------------------------------------------------------------

function transformSpecialNumber(cursor: TreeCursor, source: string): string {
  const val = text(cursor, source);
  switch (val) {
    case "Infinity":
      return '{"$numberDouble":"Infinity"}';
    case "-Infinity":
      return '{"$numberDouble":"-Infinity"}';
    case "NaN":
      return '{"$numberDouble":"NaN"}';
    default:
      return `"${val}"`;
  }
}

// ---------------------------------------------------------------------------
// Regex — /pattern/flags → $regularExpression
// ---------------------------------------------------------------------------

function transformRegex(cursor: TreeCursor, source: string): string {
  const raw = text(cursor, source);
  // Parse /pattern/flags
  const lastSlash = raw.lastIndexOf("/");
  const pattern = raw.slice(1, lastSlash);
  const options = raw.slice(lastSlash + 1);
  // Escape the pattern for JSON string embedding
  const escaped = escapeJsonString(pattern);
  return `{"$regularExpression":{"pattern":"${escaped}","options":"${options}"}}`;
}

// ---------------------------------------------------------------------------
// TemplateVariable — {{name}}, {{secret:KEY}}, {{config:KEY}}
// ---------------------------------------------------------------------------

function transformTemplateVariable(
  cursor: TreeCursor,
  source: string,
  resolver: VariableResolver | undefined,
  errors: string[],
): string {
  const raw = text(cursor, source);
  // Strip {{ and }}
  const inner = raw.slice(2, -2);

  let resolved: string | undefined;

  if (inner.startsWith("secret:")) {
    const key = inner.slice(7);
    resolved = resolver?.resolveSecret?.(key);
    if (resolved === undefined) {
      errors.push(`Unresolved secret variable: {{${inner}}}`);
      return `"{{${inner}}}"`;
    }
  } else if (inner.startsWith("config:")) {
    const key = inner.slice(7);
    resolved = resolver?.resolveConfig?.(key);
    if (resolved === undefined) {
      errors.push(`Unresolved config variable: {{${inner}}}`);
      return `"{{${inner}}}"`;
    }
  } else {
    resolved = resolver?.resolveVariable?.(inner);
    if (resolved === undefined) {
      errors.push(`Unresolved variable: {{${inner}}}`);
      return `"{{${inner}}}"`;
    }
  }

  // If the resolved value looks like valid JSON, emit it raw.
  // Otherwise, emit it as a JSON string.
  return toJsonValue(resolved);
}

// ---------------------------------------------------------------------------
// Children traversal (for Document node)
// ---------------------------------------------------------------------------

function transformChildren(
  cursor: TreeCursor,
  source: string,
  resolver: VariableResolver | undefined,
  errors: string[],
): string {
  const parts: string[] = [];

  if (cursor.firstChild()) {
    do {
      const id = cursor.type.id;
      if (isValueNode(id)) {
        parts.push(transformNode(cursor, source, resolver, errors));
      }
    } while (cursor.nextSibling());
    cursor.parent();
  }

  // A document should have a single top-level value, but handle multiple
  return parts.join(",");
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Check if a node type ID represents a value (not punctuation/structural). */
function isValueNode(id: number): boolean {
  return (
    id === ObjectNode ||
    id === ArrayNode ||
    id === StringNode ||
    id === NumberNode ||
    id === BooleanNode ||
    id === Null ||
    id === BsonConstructor ||
    id === BsonKeyword ||
    id === Regex ||
    id === TemplateVariable ||
    id === SpecialNumber ||
    id === Property // Property is a value-bearing node inside Object
  );
}

/** Extract the source text for the current cursor node. */
function text(cursor: TreeCursor, source: string): string {
  return source.slice(cursor.from, cursor.to);
}

/** Remove surrounding quotes (single or double) from a string value. */
function unquote(s: string): string {
  if (
    (s.startsWith('"') && s.endsWith('"')) ||
    (s.startsWith("'") && s.endsWith("'"))
  ) {
    const inner = s.slice(1, -1);
    // Unescape backslash-escaped quotes
    if (s.startsWith("'")) {
      return inner.replace(/\\'/g, "'");
    }
    return inner.replace(/\\"/g, '"');
  }
  // Not quoted — return as-is (e.g., for NumberLong(123))
  return s;
}

/** Normalize a date string to ISO 8601 with milliseconds. */
function normalizeDate(raw: string): string | null {
  if (!raw) return new Date(0).toISOString();
  const ts = Date.parse(raw);
  if (Number.isNaN(ts)) return null;
  return new Date(ts).toISOString();
}

/** Escape a string for embedding inside a JSON string value. */
function escapeJsonString(s: string): string {
  return s
    .replace(/\\/g, "\\\\")
    .replace(/"/g, '\\"')
    .replace(/\n/g, "\\n")
    .replace(/\r/g, "\\r")
    .replace(/\t/g, "\\t");
}

/** Convert a resolved variable value to a JSON-safe representation. */
function toJsonValue(value: string): string {
  // Check if it's already valid JSON
  try {
    JSON.parse(value);
    return value;
  } catch {
    // Not valid JSON — emit as a JSON string
    return JSON.stringify(value);
  }
}
